<template>
  <v-main class="drill-library-main">
    <div class="library-layout">
      <aside class="library-sidebar" aria-label="Drill filters">
        <span class="sidebar-label">Categories</span>
        <button
            type="button"
            class="cat"
            :class="{ on: selectedTag === null }"
            :aria-pressed="selectedTag === null"
            @click="selectedTag = null"
        >
          <span>All drills</span>
          <span class="cat-count">{{ libraryDrills.length }}</span>
        </button>
        <button
            v-for="t in tagCounts"
            :key="t.tag"
            type="button"
            class="cat"
            :class="{ on: selectedTag === t.tag }"
            :aria-pressed="selectedTag === t.tag"
            @click="selectedTag = t.tag"
        >
          <span>{{ t.tag }}</span>
          <span class="cat-count">{{ t.count }}</span>
        </button>

        <template v-if="groupCounts.length">
          <span class="sidebar-label sidebar-label-spaced">Groups</span>
          <button
              v-for="g in groupCounts"
              :key="g.group"
              type="button"
              class="cat"
              :class="{ on: selectedGroup === g.group }"
              :aria-pressed="selectedGroup === g.group"
              @click="toggleGroup(g.group)"
          >
            <span>{{ g.group }}</span>
            <span class="cat-count">{{ g.count }}</span>
          </button>
        </template>
      </aside>

      <section class="library-content">
        <div class="library-heading">
          <div>
            <h1 class="library-title">Drill Library</h1>
            <p class="library-subtitle">{{ subtitle }}</p>
          </div>
          <v-btn
              color="primary"
              variant="flat"
              class="text-none"
              prepend-icon="mdi-plus"
              :disabled="!team"
              @click="newDrill"
          >
            New Drill
          </v-btn>
        </div>

        <div class="library-toolbar">
          <v-text-field
              v-model="search"
              placeholder="Search by name or notes"
              aria-label="Search drills"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="toolbar-search"
          ></v-text-field>
          <v-select
              v-if="teams.length > 1"
              v-model="team"
              :items="teams"
              item-title="name"
              item-value="id"
              return-object
              label="Team"
              variant="outlined"
              density="compact"
              hide-details
              class="toolbar-team"
          ></v-select>
          <div class="length-filters" role="group" aria-label="Filter by length">
            <button
                v-for="option in lengthOptions"
                :key="option.id"
                type="button"
                class="chip"
                :class="{ on: selectedLength === option.id }"
                :aria-pressed="selectedLength === option.id"
                @click="selectedLength = option.id"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <v-progress-linear v-if="loading" color="primary" height="3" indeterminate></v-progress-linear>

        <div v-else-if="filteredDrills.length" class="drill-grid">
          <button
              v-for="d in filteredDrills"
              :key="d.id"
              type="button"
              class="drill-card"
              :aria-label="'Edit ' + d.name"
              @click="editDrill(d)"
          >
            <div class="thumb">
              <img v-if="d.images && d.images.length" :src="imageUrl(d.images[0])" alt="">
              <v-icon v-else size="40" class="thumb-icon">mdi-clipboard-text-outline</v-icon>
            </div>
            <div class="drill-body">
              <h2 class="drill-title">{{ d.name || 'Untitled drill' }}</h2>
              <p v-if="d.notes" class="drill-desc">{{ d.notes }}</p>
              <p v-else class="drill-desc drill-desc-empty">No notes yet</p>
              <div class="meta-row">
                <span v-for="tag in visibleTags(d)" :key="tag" class="badge">{{ tag }}</span>
                <span v-if="hiddenTagCount(d)" class="badge badge-more">+{{ hiddenTagCount(d) }}</span>
                <span class="meta-spacer"></span>
                <span v-if="d.duration" class="mins">{{ d.duration }} min</span>
                <span v-else class="meta-note">No length set</span>
              </div>
            </div>
          </button>
        </div>

        <div v-else-if="team" class="empty-state">
          <v-icon size="48" class="empty-icon">mdi-clipboard-search-outline</v-icon>
          <template v-if="hasFilters">
            <h2 class="empty-title">No drills match</h2>
            <p class="empty-text">Try a different search, or clear the filters.</p>
            <v-btn variant="outlined" color="primary" class="text-none" @click="clearFilters">Clear Filters</v-btn>
          </template>
          <template v-else>
            <h2 class="empty-title">No drills yet</h2>
            <p class="empty-text">Add your first drill to start building practice plans from it.</p>
            <v-btn variant="flat" color="primary" class="text-none" prepend-icon="mdi-plus" @click="newDrill">New Drill</v-btn>
          </template>
        </div>
      </section>
    </div>

    <v-dialog v-model="editorOpen" max-width="760" scrollable>
      <v-card v-if="editing" class="editor-card">
        <v-card-title class="editor-header">{{ editing.id ? 'Edit Drill' : 'New Drill' }}</v-card-title>
        <v-card-text class="editor-body">
          <div class="editor-row editor-row-name">
            <v-text-field
                v-model="editing.name"
                label="Drill Name"
                variant="outlined"
                density="comfortable"
            ></v-text-field>
            <v-text-field
                v-model.number="editing.duration"
                label="Minutes"
                type="number"
                min="0"
                :max="MAX_DURATION"
                step="1"
                :rules="[durationRule]"
                variant="outlined"
                density="comfortable"
                class="duration-field"
            ></v-text-field>
          </div>
          <div class="editor-row">
            <v-select
                v-model="editing.groups"
                :items="team ? team.groups : []"
                label="Groups"
                multiple
                chips
                clearable
                variant="outlined"
                density="comfortable"
            ></v-select>
            <v-select
                v-model="editing.tags"
                :items="tags"
                label="Tags"
                multiple
                chips
                clearable
                variant="outlined"
                density="comfortable"
            ></v-select>
          </div>
          <v-textarea
              v-model="editing.notes"
              label="Notes"
              rows="3"
              auto-grow
              variant="outlined"
              density="comfortable"
          ></v-textarea>

          <div class="images-heading">
            <span class="images-label">Images</span>
            <v-btn variant="outlined" color="primary" size="small" class="text-none" prepend-icon="mdi-image-plus" @click="showImageAdd = true">
              Add Image
            </v-btn>
          </div>
          <div v-if="editing.images.length" class="image-grid">
            <div v-for="(image, imageIndex) in editing.images" :key="image" class="image-tile">
              <ImageDisplay :teamId="team.id" :fileName="image"></ImageDisplay>
              <v-btn
                  icon="mdi-delete"
                  size="small"
                  variant="flat"
                  color="error"
                  class="image-remove"
                  :aria-label="'Remove image ' + (imageIndex + 1)"
                  @click="removeDrillImage(imageIndex)"
              ></v-btn>
            </div>
          </div>
          <p v-else class="drill-desc drill-desc-empty">No images.</p>
        </v-card-text>
        <v-card-actions class="editor-actions">
          <v-btn v-if="editing.id" color="error" variant="outlined" class="text-none" @click="deleteDrill">Delete</v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="text" class="text-none" @click="editorOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" class="text-none" :disabled="!editing.name || durationRule(editing.duration) !== true" @click="save">Save Drill</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showImageAdd" width="auto" min-width="300">
      <FileUpload v-if="showImageAdd && team" @uploadComplete="imageUploadComplete" :teamId="team.id"></FileUpload>
    </v-dialog>
  </v-main>
</template>

<script>

import api from '../api'
import FileUpload from "@/components/components/FileUpload.vue";
import ImageDisplay from "@/components/components/ImageDisplay.vue";

const MAX_VISIBLE_TAGS = 3;
const MAX_DURATION = 240;

const LENGTH_OPTIONS = [
  {id: 'any', label: 'Any length', matches: () => true},
  {id: 'short', label: 'Under 10 min', matches: (m) => m > 0 && m < 10},
  {id: 'medium', label: '10–20 min', matches: (m) => m >= 10 && m <= 20},
  {id: 'long', label: 'Over 20 min', matches: (m) => m > 20},
  {id: 'unset', label: 'Not set', matches: (m) => !m},
];

export default {
  name: 'DrillLibrary',
  components: {FileUpload, ImageDisplay},
  data() {
    return {
      MAX_DURATION,
      lengthOptions: LENGTH_OPTIONS,
      libraryDrills: [],
      loading: false,
      teams: [],
      team: null,
      search: '',
      selectedTag: null,
      selectedGroup: null,
      selectedLength: 'any',
      editing: null,
      editorOpen: false,
      showImageAdd: false,
      tags: ["Core", "Favorites", "Battle", "Clinic", "Conditioning", "Cradling", "Defense", "Dodging", "Footwork", "Give & Go", "Goalie", "Ground Balls", "Passing",
        "Shooting", "Offense", "Penalty Kill", "Scrimmage", "Small Area", "Transition", "Warm Up", "1 vs 1", "2 vs 0", "2 vs 1", "2 vs 2", "3 vs 2", "3 vs 3", "4 vs 3", "5 vs 3", "5 vs 4", "5 vs 5"]
    }
  },
  computed: {
    tagCounts() {
      const counts = {};
      let untagged = 0;
      this.libraryDrills.forEach((drill) => {
        if (!drill.tags || drill.tags.length === 0) {
          untagged++;
          return;
        }
        drill.tags.forEach((tag) => {
          counts[tag] = (counts[tag] || 0) + 1;
        });
      });
      const list = Object.keys(counts).sort().map((tag) => ({tag, count: counts[tag]}));
      if (untagged > 0) {
        list.push({tag: 'Untagged', count: untagged});
      }
      return list;
    },
    groupCounts() {
      if (!this.team || !this.team.groups) {
        return [];
      }
      return this.team.groups.map((group) => ({
        group,
        count: this.libraryDrills.filter((d) => d.groups && d.groups.includes(group)).length,
      }));
    },
    filteredDrills() {
      const term = (this.search || '').trim().toLowerCase();
      return this.libraryDrills
          .filter((d) => {
            if (this.selectedTag === 'Untagged') {
              if (d.tags && d.tags.length) return false;
            } else if (this.selectedTag && !(d.tags && d.tags.includes(this.selectedTag))) {
              return false;
            }
            if (this.selectedGroup && !(d.groups && d.groups.includes(this.selectedGroup))) {
              return false;
            }
            const length = LENGTH_OPTIONS.find((o) => o.id === this.selectedLength);
            if (length && !length.matches(d.duration || 0)) {
              return false;
            }
            if (term) {
              const name = (d.name || '').toLowerCase();
              const notes = (d.notes || '').toLowerCase();
              return name.includes(term) || notes.includes(term);
            }
            return true;
          })
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    hasFilters() {
      return !!(this.selectedTag || this.selectedGroup || this.selectedLength !== 'any' || (this.search && this.search.trim()));
    },
    subtitle() {
      if (!this.team) {
        return 'Loading teams…';
      }
      const count = this.libraryDrills.length;
      return `${this.team.name} · ${count} ${count === 1 ? 'drill' : 'drills'}`;
    },
  },
  watch: {
    team: {
      handler() {
        this.clearFilters();
        this.loadDrills();
      },
      immediate: true,
    },
    editorOpen(open) {
      if (!open) {
        this.editing = null;
      }
    },
  },
  created() {
    api.me.get((me, err) => {
      if (err) {
        this.$toast.open({message: 'Unable to load your teams', type: 'error'});
        return;
      }
      this.teams = me.teams || [];
      if (this.teams.length > 0) {
        this.team = this.teams[0];
      }
    });
  },
  methods: {
    loadDrills() {
      if (!this.team) {
        return;
      }
      this.loading = true;
      api.drills.listForTeam(this.team.id, (drills) => {
        this.libraryDrills = drills || [];
        this.loading = false;
      }, () => {
        this.loading = false;
        this.$toast.open({message: 'Unable to load drills', type: 'error'});
      });
    },
    toggleGroup(group) {
      this.selectedGroup = this.selectedGroup === group ? null : group;
    },
    clearFilters() {
      this.search = '';
      this.selectedTag = null;
      this.selectedGroup = null;
      this.selectedLength = 'any';
    },
    durationRule(value) {
      if (value === '' || value === null || value === undefined) {
        return true;
      }
      const n = Number(value);
      if (!Number.isInteger(n) || n < 0 || n > MAX_DURATION) {
        return `Enter whole minutes from 0 to ${MAX_DURATION}`;
      }
      return true;
    },
    imageUrl(fileName) {
      if (fileName.indexOf("https://") === 0) {
        return fileName;
      }
      return `/api/teams/${this.team.id}/images/${fileName}`;
    },
    visibleTags(drill) {
      return (drill.tags || []).slice(0, MAX_VISIBLE_TAGS);
    },
    hiddenTagCount(drill) {
      return Math.max(0, (drill.tags || []).length - MAX_VISIBLE_TAGS);
    },
    newDrill() {
      this.editing = {name: '', notes: '', duration: null, groups: [], tags: [], images: [], links: []};
      this.editorOpen = true;
    },
    editDrill(drill) {
      const copy = JSON.parse(JSON.stringify(drill));
      copy.groups = copy.groups || [];
      copy.tags = copy.tags || [];
      copy.images = copy.images || [];
      copy.duration = copy.duration || null;
      this.editing = copy;
      this.editorOpen = true;
    },
    imageUploadComplete(id) {
      this.showImageAdd = false;
      this.editing.images.push(id);
    },
    removeDrillImage(imageIndex) {
      this.editing.images.splice(imageIndex, 1);
    },
    save() {
      const drill = {...this.editing, duration: Number(this.editing.duration) || 0};
      api.drills.save(this.team.id, drill, () => {
        this.$toast.open({message: 'Drill saved', type: 'success'});
        this.editorOpen = false;
        this.loadDrills();
      }, () => {
        this.$toast.open({message: 'Unable to save drill', type: 'error'});
      });
    },
    deleteDrill() {
      api.drills.delete(this.team.id, this.editing.id, () => {
        this.$toast.open({message: 'Drill deleted', type: 'success'});
        this.editorOpen = false;
        this.loadDrills();
      }, () => {
        this.$toast.open({message: 'Unable to delete drill', type: 'error'});
      });
    },
  }
}
</script>

<style scoped>
.drill-library-main {
  background: var(--surface-sunken);
  min-height: 100vh;
}

.library-layout {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  min-height: calc(100vh - 64px);
}

/* Sidebar: flat chrome (radius-none) */
.library-sidebar {
  flex: 1 1 220px;
  max-width: 240px;
  background: var(--surface);
  border-right: 1px solid var(--border-subtle);
  padding: var(--space-5) 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar-label {
  padding: 0 var(--space-4) var(--space-2);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.sidebar-label-spaced {
  padding-top: var(--space-5);
}

.cat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  width: 100%;
  padding: 10px var(--space-4);
  border: none;
  border-radius: var(--radius-none);
  background: transparent;
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}

.cat:hover {
  background: var(--surface-sunken);
}

.cat.on {
  background: rgba(125, 70, 186, 0.1);
  color: var(--brand-primary);
  font-weight: 600;
}

.cat:focus-visible,
.drill-card:focus-visible {
  outline: 2px solid var(--brand-primary);
  outline-offset: -2px;
}

.cat-count {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--ink-muted);
}

/* Content */
.library-content {
  flex: 999 1 560px;
  min-width: 0;
  padding: var(--space-6) 32px 48px;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.library-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.library-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--ink);
}

.library-subtitle {
  margin: var(--space-1) 0 0;
  font-size: 13px;
  color: var(--ink-muted);
}

.library-toolbar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-3);
}

.toolbar-search {
  flex: 1 1 260px;
}

.toolbar-team {
  flex: 0 1 220px;
}

/* Cards: rounded content (radius-md) */
.drill-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--space-5);
}

.drill-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  font-family: var(--font-sans);
  text-align: left;
  color: var(--ink);
  cursor: pointer;
  transition: box-shadow 0.3s ease;
}

.drill-card:hover {
  box-shadow: var(--shadow-lg);
}

.thumb {
  height: 132px;
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  border-bottom: 1px solid var(--border-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-icon {
  color: var(--brand-ink);
  opacity: 0.6;
}

.drill-body {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1;
  width: 100%;
  box-sizing: border-box;
}

.drill-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
}

.drill-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--ink-muted);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
}

.drill-desc-empty {
  font-style: italic;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: var(--space-2);
}

.badge {
  display: inline-flex;
  padding: var(--space-1) 10px;
  border-radius: var(--radius-xs);
  background: rgba(125, 70, 186, 0.12);
  color: var(--brand-primary);
  font-size: 12px;
  font-weight: 500;
}

.badge-more {
  background: var(--surface-sunken-end);
  color: var(--ink);
}

.meta-note {
  font-size: 12px;
  color: var(--ink-muted);
}

.meta-spacer {
  flex: 1;
}

.mins {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--ink-muted);
}

.length-filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.chip {
  padding: 6px 14px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--surface);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
}

.chip.on {
  background: var(--ink);
  border-color: var(--ink);
  color: var(--on-brand);
}

.chip:focus-visible {
  outline: 2px solid var(--brand-primary);
  outline-offset: 2px;
}

.editor-row-name {
  grid-template-columns: minmax(0, 1fr) 140px;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-8) var(--space-6);
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  text-align: center;
}

.empty-icon {
  color: var(--ink-muted);
}

.empty-title {
  margin: var(--space-2) 0 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--ink);
}

.empty-text {
  margin: 0 0 var(--space-3);
  font-size: 14px;
  color: var(--ink-muted);
}

/* Editor dialog */
.editor-card {
  border-radius: var(--radius-md) !important;
}

.editor-header {
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  border-bottom: 1px solid var(--border-subtle);
  padding: var(--space-5) var(--space-6) !important;
  font-weight: 600;
}

.editor-body {
  padding: var(--space-6) !important;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.editor-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
}

.images-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-3);
}

.images-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-3);
}

.image-tile {
  position: relative;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  overflow: hidden;
  display: flex;
  justify-content: center;
  background: var(--surface-sunken);
}

.image-tile :deep(img) {
  max-width: 100%;
  object-fit: cover;
  cursor: zoom-in;
}

.image-remove {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
}

.editor-actions {
  padding: var(--space-4) var(--space-6) !important;
  border-top: 1px solid var(--border-subtle);
}

@media (max-width: 760px) {
  .library-sidebar {
    flex: 1 1 100%;
    max-width: none;
    flex-direction: row;
    align-items: center;
    gap: var(--space-2);
    overflow-x: auto;
    padding: var(--space-3) var(--space-4);
    border-right: none;
    border-bottom: 1px solid var(--border-subtle);
  }

  .sidebar-label,
  .sidebar-label-spaced {
    flex: none;
    padding: 0 0 0 var(--space-2);
  }

  .cat {
    flex: none;
    width: auto;
    padding: 6px 12px;
    border: 1px solid var(--border);
    border-radius: 999px;
    white-space: nowrap;
  }

  .library-content {
    padding: var(--space-5) var(--space-4) 40px;
  }

  .editor-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
}
</style>
